import { useMemo } from "react";
import type { RegionNode } from "../../api/regions.service";
import { SearchableSelect } from "../../components/Form/SearchableSelect";
import {
  buildRegionNodeTree,
  regionNodeKey,
  useRegionNodes,
  type RegionNodeTreeNode,
} from "../../hooks/useRegions";

export type RegionNodeRef = { kind: RegionNode["kind"]; id: number };

const KIND_LABELS: Record<RegionNode["kind"], string> = { region: "Region", country: "Country" };

function flatten(
  nodes: RegionNodeTreeNode[],
  parent: RegionNode | null = null,
): Array<{ node: RegionNode; parent: RegionNode | null }> {
  return nodes.flatMap(node => [{ node, parent }, ...flatten(node.children, node)]);
}

export function regionNodeLabel(node: Pick<RegionNode, "kind" | "name">): string {
  return `${node.name ?? "—"} (${KIND_LABELS[node.kind]})`;
}

export function useRegionNodeNames() {
  const { nodes } = useRegionNodes();
  return useMemo(() => new Map(nodes.map(node => [regionNodeKey(node), node])), [nodes]);
}

type RegionNodeSelectProps = {
  id: string;
  value: RegionNodeRef | null;
  onChange: (node: RegionNode | null) => void;
  exclude?: ReadonlySet<string>;
  disabled?: boolean;
};

export function RegionNodeSelect({
  id,
  value,
  onChange,
  exclude,
  disabled,
}: RegionNodeSelectProps) {
  const { nodes, loading, error } = useRegionNodes();
  const options = useMemo(
    () =>
      flatten(buildRegionNodeTree(nodes)).map(({ node, parent }) => {
        const mapped = exclude?.has(regionNodeKey(node)) ?? false;
        const notes = [
          parent ? `In ${parent.name ?? "—"}` : null,
          mapped ? "Already mapped" : null,
        ].filter((note): note is string => note !== null);
        return {
          value: regionNodeKey(node),
          label: regionNodeLabel(node),
          description: notes.length > 0 ? notes.join(" · ") : undefined,
          disabled: mapped,
        };
      }),
    [nodes, exclude],
  );
  const byKey = useMemo(() => new Map(nodes.map(node => [regionNodeKey(node), node])), [nodes]);

  return (
    <SearchableSelect
      name={id}
      label="Region or country page"
      value={value ? regionNodeKey(value) : null}
      onChange={key => onChange(key === null ? null : (byKey.get(String(key)) ?? null))}
      disabled={disabled || loading}
      error={error ? `Failed to load regions: ${error.message}` : undefined}
      placeholder={loading ? "Loading regions..." : "Choose a page"}
      options={options}
      preserveOptionOrder
    />
  );
}
